import { Auth, API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { useDispatch, useSelector } from "react-redux";
import { setListing, setSearchText, setSelected } from "../store/ducks/admins";
import { HeadCell } from "../models/dataTable";
import { AdminsApiPaths, AdminsConstants } from "../constants/enums";
import useApp from "./useApp";
import useAccount from "./useAccount";
import { AdminGroup } from "../models";
import { UpdateAdminGroupInput } from "../models/GQL_API";
import { listAdminGroupsCustom } from "../graphqlCustom/queries";
import { updateAdminGroup } from "../graphql/mutations";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showError, showConfirm, session } = useApp();
  const { accountsGetDomainName } = useAccount("accounts", "account");

  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]["nextToken"]
  );

  const COGNITO_GROUPS = [{ label: "admin", value: "admin" }];

  /**
   * Add user to AdminGroup table in DynamoDB
   * @param userSub - The Cognito UserSub (user ID)
   * @param groupName - The AdminGroup name to add the user to
   */
  async function addUserToAdminGroupTable(userSub: string, groupName: string) {
    try {
      // Find the AdminGroup by name
      const filter: any = {
        deleted: { eq: "0" },
        name: { eq: groupName.toLowerCase() },
      };

      const groupsList: any = await API.graphql<AdminGroup>({
        query: listAdminGroupsCustom,
        variables: { filter },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const groups = groupsList.data.listAdminGroups.items;

      if (groups && groups.length > 0) {
        const adminGroup = groups[0];
        const existingUsers = adminGroup.users || [];

        // Check if user is already in the group
        if (!existingUsers.includes(userSub)) {
          const updateInput: UpdateAdminGroupInput = {
            id: adminGroup.id,
            users: [...existingUsers, userSub],
          };

          await API.graphql<AdminGroup>({
            query: updateAdminGroup,
            variables: { input: updateInput },
            authMode: session
              ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
              : GRAPHQL_AUTH_MODE.AWS_IAM,
          });

        }
      } else {
      }
    } catch (err: Error | any) {
      throw err;
    }
  }

  async function create(data: any) {
    try {
      const domain = await accountsGetDomainName();

      const user = await Auth.signUp({
        username: `${domain}_${data.email}`,
        password: data.password,
        attributes: {
          name: data.name,
          email: `${domain}_${data.email}`,
        },
      });

      // Get the UserSub from the signUp response
      const userSub = user.userSub;
      const groupName = data.group ? data.group : "admin";

      // Add user to Cognito group
      await addUserToGroup(user.user.getUsername(), groupName);

      // Add UserSub to AdminGroup table in DynamoDB
      await addUserToAdminGroupTable(userSub, groupName);

      showConfirm(`User created and added to ${groupName} group successfully`);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function adminsConfig(apiPath: string, queryString: any, body?: any) {
    let apiName = AdminsConstants.ADMINS_API;
    let path = apiPath;
    let myInit = {
      body: body,
      queryStringParameters: queryString,
      headers: {
        "Content-Type": "application/json",
        Authorization: `${(await Auth.currentSession())
          .getAccessToken()
          .getJwtToken()}`,
      },
    };

    return {
      apiName,
      path,
      myInit,
    };
  }

  async function listGroupsForUser(username: string) {
    let groups: any[] = [];
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_GROUPS_FOR_USER,
        {
          username,
        }
      );
      const { Groups } = await API.get(apiName, path, myInit);
      for (let group of Groups) {
        groups.push(group["GroupName"]);
      }
      return groups;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function listAllGroups() {
    let groups: any[] = [];
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_GROUPS,
        { limit: 60 }
      );
      const { Groups } = await API.get(apiName, path, myInit);
      for (let group of Groups) {
        groups.push(group["GroupName"]);
      }
      return groups;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function addUserToGroup(username: string, groupname: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.ADD_USER_TO_GROUP,
        {},
        {
          username,
          groupname,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      showConfirm(result.message);
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  async function removeUserFromGroup(username: string, groupname: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.REMOVE_USER_FROM_GROUP,
        {},
        {
          username,
          groupname,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      showConfirm(result.message);
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  async function fetch(nextToken: string, limit: number) {
    try {
      // Filter users by account domain
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_USERS,
        {
          limit: limit,
          token: nextToken,
        }
      );

      const { NextToken, ...data } = await API.get(apiName, path, myInit);
      nextToken = NextToken === undefined ? "" : NextToken;
      let listing: any[] = [];
      let cognitoObj = data.Users;
      const domain = await accountsGetDomainName();

      for (let user of cognitoObj) {
        // Skip disabled users
        if (user.Enabled === false) {
          continue;
        }
        
        let { Attributes, ...userData } = user;

        for (let attr of user["Attributes"]) {
          userData[attr["Name"]] = attr["Value"];
          if (userData["Username"]) {
            userData["id"] = userData["Username"];
          }
          if (userData["UserCreateDate"]) {
            userData["createdAt"] = userData["UserCreateDate"];
          }
          if (userData["email"]) {
            userData["email"] = userData["email"].split(`${domain}_`)[1];
          }
        }
        // get user groups
        // if (userData["Username"]) {
        //   userData["Groups"] = await listGroupsForUser(userData["Username"]);
        // }
        listing.push(userData);
      }
      dispatch(setListing(listing));
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function byGroup(nextToken: string, limit: number, groupName: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.LIST_USERS_IN_GROUP,
        {
          groupname: groupName,
          limit: limit,
          token: nextToken,
        }
      );

      const { NextToken, ...data } = await API.get(apiName, path, myInit);
      nextToken = NextToken;
      const listing = data.Users;

      dispatch(setListing(listing));
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function get(username: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.GET_USER,
        {
          username,
        }
      );

      const { ...single } = await API.get(apiName, path, myInit);
      let { UserAttributes, ...userData } = single;
      const domain = await accountsGetDomainName();

      for (let attr of single["UserAttributes"]) {
        userData[attr["Name"]] = attr["Value"];
        if (userData["Username"]) {
          userData["id"] = userData["Username"];
        }
        if (userData["UserCreateDate"]) {
          userData["createdAt"] = userData["UserCreateDate"];
        }
        if (userData["email"]) {
          userData["email"] = userData["email"].split(`${domain}_`)[1];
        }

        // get user groups
      }
      if (userData["Username"]) {
        userData["Groups"] = await listGroupsForUser(userData["Username"]);
      }
      return userData;
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  async function enableUser(username: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.ENABLE_USER,
        {},
        {
          username,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);

      showConfirm("User enabled successfully.");
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  async function disableUser(username: string) {
    try {
      const { apiName, path, myInit } = await adminsConfig(
        AdminsApiPaths.DISABLE_USER,
        {},
        {
          username,
        }
      );
      const { ...result } = await API.post(apiName, path, myInit);
      
      // Remove the disabled user from the listing
      const updatedListing = listing.filter(
        (user: any) => user.Username !== username && user.id !== username
      );
      dispatch(setListing(updatedListing));
      
      showConfirm("User disabled and removed from list successfully.");
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  async function changePassword(oldPassword: string, newPassword: string) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Auth.changePassword(user, oldPassword, newPassword);
      showConfirm(result);
    } catch (err: Error | any) {
      showError(
        typeof err.message === "string" ? err.message : "Error occurred"
      );
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: "Email",
    },
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["email", "name"];

  function isAdmin(session: any) {
    if (session && session["cognito:groups"]) {
      return (
        session["cognito:groups"].find((group: any) => group === "admin") !==
        undefined
      );
    }
  }

  async function isCurrentAuthUser(resourceUsername: string): Promise<boolean> {
    const { username } = await Auth.currentAuthenticatedUser();
    return username === resourceUsername;
  }

  const options: any[] = [];

  for (let option of listing) {
    options.push({ label: option.name, value: option.sub });
  }
  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;

  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchByGroup`] = byGroup;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = () => {};
  api[`${listingName}Trash`] = () => {};
  api[`${listingName}BulkTrash`] = () => {};
  api[`${listingName}Delete`] = () => {};
  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api["isAdmin"] = isAdmin;
  api["isCurrentAuthUser"] = isCurrentAuthUser;
  api["changePassword"] = changePassword;
  api[`${listingName}ListGroupsForUser`] = listGroupsForUser;
  api[`${listingName}ListAllGroups`] = listAllGroups;
  api[`addUserToGroup`] = addUserToGroup;
  api[`removeUserFromGroup`] = removeUserFromGroup;
  api[`${listingName}DisableUser`] = disableUser;
  api[`${listingName}EnableUser`] = enableUser;
  api[`${listingName}CognitoGroups`] = COGNITO_GROUPS;

  return api;
};

export default useResource;
